// Source-neutral presentation: provider adaptation and mutations live outside.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../../app/tokens.js";
import { Modal } from "../../../components/Modal.jsx";
import { btn, muted } from "../weekly/weeklyUi.js";

export function safeMediaUrl(url) {
  if (typeof url !== "string" || /[\u0000-\u0020\\]/.test(url)) return null;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  try { return new URL(url).protocol === "https:" ? url : null; } catch { return null; }
}

function ImageFile({ item, large = false, photo = false }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [item.url]);
  const src = safeMediaUrl(item.url);
  return !src || broken
    ? <div role="status" style={{ ...muted, padding: 16 }}>Image unavailable. Try opening or downloading the file.</div>
    : <img src={src} alt={item.caption} onError={() => setBroken(true)} style={{ display: "block", width: "100%", height: large ? "auto" : photo ? "100%" : 82, maxHeight: large ? "60vh" : undefined, objectFit: large ? "contain" : "cover" }} />;
}

function MediaLightbox({ images, activeId, onIndex, onClose }) {
  const panel = useRef(null);
  const close = useRef(null);
  const titleId = useId();
  const index = images.findIndex(item => item.id === activeId);
  const item = images[index];
  const open = Boolean(item);
  useEffect(() => {
    if (!open) return undefined;
    // Modal captures the triggering focus first; focusing synchronously in a
    // child effect would make its focus-return target the Close button.
    const timer = setTimeout(() => close.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);
  const keyDown = event => {
    if (event.key === "ArrowLeft" && index > 0) { event.preventDefault(); onIndex(images[index - 1].id); }
    if (event.key === "ArrowRight" && index < images.length - 1) { event.preventDefault(); onIndex(images[index + 1].id); }
    if (event.key === "Tab") {
      const nodes = [...panel.current.querySelectorAll("button:not(:disabled),a[href]")];
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };
  // Escape the sticky media column's stacking context, not the layer scale.
  return createPortal(<Modal isOpen={Boolean(item)} onClose={onClose} maxWidth={800} ariaLabelledBy={titleId} testId="media-lightbox">
    {item && <div ref={panel} onKeyDown={keyDown} style={{ padding: 16, overflowY: "auto" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <h3 id={titleId} style={{ flex: 1, margin: 0, fontSize: T.fsMd, overflowWrap: "anywhere" }}>{item.caption}</h3>
        <span aria-live="polite" data-testid="media-position">{index + 1} of {images.length}</span>
        <button ref={close} type="button" onClick={onClose} style={btn}>Close</button>
      </div>
      <ImageFile key={item.id} item={item} large />
      <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 12 }}>
        <button type="button" style={btn} disabled={index === 0} onClick={() => onIndex(images[index - 1].id)}>Previous</button>
        {safeMediaUrl(item.downloadUrl || item.url) && <a href={safeMediaUrl(item.downloadUrl || item.url)} download style={{ color: T.blue }}>Download image</a>}
        <button type="button" style={btn} disabled={index === images.length - 1} onClick={() => onIndex(images[index + 1].id)}>Next</button>
      </div>
    </div>}
  </Modal>, document.body);
}

export function mediaKindLabel(item) {
  if (item?.kind === "plan") return "Plan";
  if (item?.kind === "photo") return "Photo";
  return "File";
}

// Starred items (image IDs, in star order) pin above the rest; unstarred keep
// their relative order. Stale IDs never surface or take a rank.
export function orderByStars(items = [], stars = []) {
  const byId = new Map(items.map(item => [item.id, item]));
  const pinned = [...new Set(stars)].map(id => byId.get(id)).filter(item => item && item.type === "image").slice(0, 3);
  const pinnedIds = new Set(pinned.map(item => item.id));
  return [...pinned, ...items.filter(item => !pinnedIds.has(item.id))];
}

const thumbFallback = item => <div aria-hidden="true" style={{ height: "100%", minHeight: 52, display: "grid", placeItems: "center", background: T.lightGray, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.mid }}>{item.type === "pdf" ? "PDF" : "Doc"}</div>;

function StarControl({ item, rank, onToggleStar }) {
  if (!onToggleStar || item.type !== "image") return null;
  const on = rank > 0;
  return <button type="button" data-testid={`media-star-${item.id}`} aria-pressed={on} aria-label={on ? `Unstar ${item.caption} (pinned ${rank})` : `Star ${item.caption}`} title={on ? "Unstar" : "Star to pin (up to 3)"} onClick={() => onToggleStar(item.id)}
    style={{ border: `1px solid ${on ? T.blue : T.border}`, background: on ? T.blueSoft : T.card, color: on ? T.blue : T.mid, borderRadius: 6, width: 26, height: 24, padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" /></svg>
  </button>;
}

const RankBadge = ({ id, rank }) => rank > 0 ? <span data-testid={`media-rank-${id}`} aria-hidden="true" style={{ position: "absolute", top: 5, left: 5, minWidth: 18, height: 18, borderRadius: 9, background: T.blue, color: T.white, fontSize: T.fsXxs, fontWeight: T.fwBold, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 5px", boxSizing: "border-box" }}>{rank}</span> : null;

export function MediaStrip({ items = [], renderActions, emptyText = "No floor plans or photos attached to this project.", twoUp = false, trailing = null, layout = null, stars = null, onToggleStar = null, hideKind = false, menuActions = null }) {
  const [activeId, setActiveId] = useState(null);
  const ordered = useMemo(() => (stars ? orderByStars(items, stars) : items), [items, stars]);
  const images = useMemo(() => ordered.filter(item => item.type === "image"), [ordered]);
  const validStars = stars ? ordered.filter(item => item.type === "image" && stars.includes(item.id)).slice(0, 3).map(item => item.id) : [];
  const rankOf = id => validStars.indexOf(id) + 1;
  const links = item => <div style={{ display: "flex", gap: 8 }}>
    {safeMediaUrl(item.url) && <a href={safeMediaUrl(item.url)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${item.caption}`} style={{ color: T.blue }}>Open</a>}
    {safeMediaUrl(item.downloadUrl || item.url) && <a href={safeMediaUrl(item.downloadUrl || item.url)} download aria-label={`Download ${item.caption}`} style={{ color: T.blue }}>Download</a>}
  </div>;
  const thumb = item => item.type === "image"
    ? <button type="button" aria-label={`Open photo: ${item.caption}`} onClick={() => setActiveId(item.id)} style={{ display: "block", width: "100%", height: "100%", padding: 0, border: 0, background: T.lightGray, cursor: "pointer" }}><ImageFile item={item} photo={twoUp && layout === "tile"} /></button>
    : thumbFallback(item);
  if (layout === "list" || layout === "tile") {
    const list = layout === "list";
    return <div>
      {twoUp && <style>{`
        .updates-photo { position: relative; }
        .updates-photo .photo-caption { position: absolute; left: 0; right: 0; bottom: 0; padding: 18px 8px 6px; background: linear-gradient(transparent, rgba(15, 30, 50, .72)); color: ${T.white}; font-size: ${T.fsXs}px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0; pointer-events: none; transition: opacity .15s; }
        .updates-photo:hover .photo-caption, .updates-photo:focus-within .photo-caption, .updates-photo[data-open="true"] .photo-caption { opacity: 1; }
        .updates-photo .photo-more { position: absolute; right: 6px; top: 6px; z-index: 1; opacity: 0; transition: opacity .15s; }
        .updates-photo .photo-star { position: absolute; right: 44px; top: 6px; z-index: 1; opacity: 0; transition: opacity .15s; }
        .updates-photo:hover .photo-more, .updates-photo:focus-within .photo-more, .updates-photo[data-open="true"] .photo-more,
        .updates-photo:hover .photo-star, .updates-photo:focus-within .photo-star, .updates-photo[data-starred="true"] .photo-star { opacity: 1; }
        @media (hover: none), (pointer: coarse) { .updates-photo .photo-more, .updates-photo .photo-star { opacity: 1; min-width: 32px; min-height: 32px; } .updates-photo .photo-caption { opacity: 1; } }
      `}</style>}
      {items.length === 0 && !trailing && <p style={muted}>{emptyText}</p>}
      <div data-testid={`media-layout-${layout}`} style={list ? { display: "flex", flexDirection: "column", gap: 8 } : { display: "grid", gridTemplateColumns: twoUp ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, minmax(118px, 1fr))", gap: 10 }}>
        {ordered.map(item => {
          const rank = rankOf(item.id);
          if (twoUp && item.type === "image") return <PhotoTile key={item.id} item={item} rank={rank} thumb={thumb(item)} links={links(item)} actions={renderActions?.(item)} menuActions={menuActions} onToggleStar={onToggleStar} list={list} />;
          return <div key={item.id} data-testid={`media-item-${item.id}`} data-rank={rank || undefined} style={{ minWidth: 0, border: `1px solid ${rank ? T.blue : T.border}`, borderRadius: 8, overflow: "hidden", background: T.card, display: list ? "flex" : "block" }}>
            <div style={{ position: "relative", flexShrink: 0, width: list ? 76 : "100%", height: list ? 62 : 82, overflow: "hidden" }}>{thumb(item)}<RankBadge id={item.id} rank={rank} /></div>
            <div style={{ padding: 8, fontSize: T.fsXs, overflowWrap: "anywhere", flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <div style={{ color: T.dark, marginBottom: 4, flex: 1, minWidth: 0 }}>{item.caption}</div>
                <StarControl item={item} rank={rank} onToggleStar={onToggleStar} />
              </div>
              {!hideKind && <div data-testid={`media-kind-${item.id}`} style={{ ...muted, fontSize: T.fsXxs }}>{mediaKindLabel(item)}</div>}
              {item.illustrative && <span style={muted}>Illustrative demo asset</span>}
              {links(item)}
              {renderActions?.(item)}
            </div>
          </div>;
        })}
        {trailing}
      </div>
      <MediaLightbox images={images} activeId={activeId} onIndex={setActiveId} onClose={() => setActiveId(null)} />
    </div>;
  }
  return <div>
    {items.length === 0 && !trailing && <p style={muted}>{emptyText}</p>}
    <div data-testid={twoUp ? "media-grid-two-up" : undefined} style={twoUp ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 } : { display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
      {ordered.map(item => <div key={item.id} data-testid={`media-item-${item.id}`} style={{ flexShrink: 0, width: twoUp ? "auto" : 156, minWidth: 0, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", background: T.card }}>
        {item.type === "image"
          ? <button type="button" aria-label={`Open photo: ${item.caption}`} onClick={() => setActiveId(item.id)} style={{ display: "block", width: "100%", padding: 0, border: 0, background: T.lightGray, cursor: "pointer" }}><ImageFile item={item} /></button>
          : <div style={{ height: 82, display: "grid", placeItems: "center", background: T.lightGray, fontSize: T.fsMd, fontWeight: T.fwBold }}>PDF</div>}
        <div style={{ padding: 8, fontSize: T.fsXs, overflowWrap: "anywhere" }}>
          {twoUp && !hideKind && <div data-testid={`media-kind-${item.id}`} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".05em", color: T.mid }}>{mediaKindLabel(item)}</div>}
          <div style={{ color: T.dark, marginBottom: 6 }}>{item.caption}</div>
          {item.illustrative && <span style={muted}>Illustrative demo asset</span>}
          {links(item)}
          {renderActions?.(item)}
        </div>
      </div>)}
      {trailing}
    </div>
    <MediaLightbox images={images} activeId={activeId} onIndex={setActiveId} onClose={() => setActiveId(null)} />
  </div>;
}

// Menu entries: safe Open/Download links, authorized extra actions, Unstar.
function menuEntries(item, rank, menuActions, onToggleStar) {
  const entries = [];
  const open = safeMediaUrl(item.url);
  const download = safeMediaUrl(item.downloadUrl || item.url);
  if (open) entries.push({ key: "open", label: "Open", href: open, target: "_blank" });
  if (download) entries.push({ key: "download", label: "Download", href: download, download: true });
  for (const action of menuActions?.(item) || []) entries.push(action);
  if (rank > 0 && onToggleStar) entries.push({ key: "unstar", label: "Unstar", onSelect: () => onToggleStar(item.id) });
  return entries;
}

// Portaled to <body> with fixed positioning so tile overflow and neighbouring
// tiles can never clip it. Outside pointer, Escape, scroll and resize close it.
function PhotoMenu({ item, anchor, entries, onClose }) {
  const menu = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (!r) return;
    const width = 168;
    setPos({ top: Math.min(r.bottom + 4, window.innerHeight - 8 - entries.length * 34), left: Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8)), width });
  }, [anchor, entries.length]);
  useEffect(() => { menu.current?.querySelector("[role='menuitem']")?.focus(); }, [pos]);
  useEffect(() => {
    const down = event => { if (!menu.current?.contains(event.target) && !anchor.current?.contains(event.target)) onClose(false); };
    const key = event => { if (event.key === "Escape") { event.preventDefault(); onClose(true); } };
    const focusAway = event => { if (!menu.current?.contains(event.target) && !anchor.current?.contains(event.target)) onClose(false); };
    const away = event => { if (!menu.current?.contains(event.target)) onClose(false); };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("mousedown", down, true);
    document.addEventListener("keydown", key);
    document.addEventListener("focusin", focusAway);
    window.addEventListener("resize", away);
    window.addEventListener("scroll", away, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("mousedown", down, true);
      document.removeEventListener("keydown", key);
      document.removeEventListener("focusin", focusAway);
      window.removeEventListener("resize", away);
      window.removeEventListener("scroll", away, true);
    };
  }, [anchor, onClose]);
  const keyNav = event => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const nodes = [...menu.current.querySelectorAll("[role='menuitem']")];
    const i = nodes.indexOf(document.activeElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? nodes.length - 1 : (i + (event.key === "ArrowDown" ? 1 : -1) + nodes.length) % nodes.length;
    nodes[next]?.focus();
  };
  const itemStyle = { display: "block", width: "100%", textAlign: "left", padding: "7px 12px", border: 0, background: "transparent", color: T.dark, fontSize: T.fsSm, textDecoration: "none", cursor: "pointer", boxSizing: "border-box" };
  return createPortal(<div ref={menu} role="menu" aria-label={`Actions for ${item.caption}`} data-testid={`media-menu-${item.id}`} onKeyDown={keyNav}
    style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: pos?.width ?? 168, zIndex: 2000, background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, boxShadow: T.projectsMenuShadow, padding: "4px 0" }}>
    {entries.map(entry => entry.href
      ? <a key={entry.key} role="menuitem" tabIndex={-1} data-testid={`media-menu-${entry.key}-${item.id}`} href={entry.href} target={entry.target} rel={entry.target ? "noopener noreferrer" : undefined} download={entry.download || undefined} onClick={() => onClose(false)} style={itemStyle}>{entry.label}</a>
      : <button key={entry.key} type="button" role="menuitem" tabIndex={-1} data-testid={`media-menu-${entry.key}-${item.id}`} disabled={entry.disabled} onClick={() => { onClose(false); entry.onSelect(); }} style={{ ...itemStyle, color: entry.danger ? T.red : T.dark, opacity: entry.disabled ? 0.5 : 1 }}>{entry.label}</button>)}
  </div>, document.body);
}

function PhotoTile({ item, rank, thumb, links, actions, menuActions, onToggleStar, list }) {
  const [open, setOpen] = useState(false);
  const more = useRef(null);
  if (list) return <LegacyPhotoRow item={item} rank={rank} thumb={thumb} links={links} actions={actions} onToggleStar={onToggleStar} />;
  const entries = menuEntries(item, rank, menuActions, onToggleStar);
  const close = refocus => { setOpen(false); if (refocus) more.current?.focus(); };
  return <div className="updates-photo" data-testid={`media-item-${item.id}`} data-rank={rank || undefined} data-open={open || undefined} data-starred={rank > 0 || undefined}
    style={{ minWidth: 0, border: `1px solid ${rank ? T.blue : T.border}`, borderRadius: 9, overflow: "hidden", aspectRatio: "4 / 3", background: T.lightGray }}>
    <div style={{ width: "100%", height: "100%" }}>{thumb}</div>
    <span className="photo-caption" data-testid={`media-caption-${item.id}`} title={item.caption}>{item.caption}{item.illustrative ? " · Illustrative demo asset" : ""}</span>
    <RankBadge id={item.id} rank={rank} />
    {onToggleStar && <span className="photo-star"><StarControl item={item} rank={rank} onToggleStar={onToggleStar} /></span>}
    <button ref={more} type="button" className="photo-more" data-testid={`media-more-${item.id}`} aria-label={`Image actions for ${item.caption}`} aria-haspopup="menu" aria-expanded={open}
      style={{ ...btn, padding: "0 8px", height: 26, lineHeight: 1 }} onClick={event => { event.stopPropagation(); setOpen(value => !value); }}>⋯</button>
    {open && entries.length > 0 && <PhotoMenu item={item} anchor={more} entries={entries} onClose={close} />}
  </div>;
}

function LegacyPhotoRow({ item, rank, thumb, links, actions, onToggleStar }) {
  return <div data-testid={`media-item-${item.id}`} data-rank={rank || undefined} style={{ position: "relative", display: "flex", minWidth: 0, border: `1px solid ${rank ? T.blue : T.border}`, borderRadius: 9, overflow: "hidden", background: T.card }}>
    <div style={{ width: 88, height: 82, flexShrink: 0 }}>{thumb}</div>
    <RankBadge id={item.id} rank={rank} />
    <div style={{ padding: 8, flex: 1, minWidth: 0, fontSize: T.fsXs }}>
      <div style={{ display: "flex", gap: 6 }}><span style={{ flex: 1, overflowWrap: "anywhere" }}>{item.caption}</span><StarControl item={item} rank={rank} onToggleStar={onToggleStar} /></div>
      {links}{actions}
    </div>
  </div>;
}
